import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { JSX as CuiModalJSX } from '@congstar/cui-modal/dist/loader';
import './PasswordRenew.scss';
import {
  Password,
  PATTERN as PasswordPattern,
  MIN_LENGTH as PasswordMinLength,
  MAX_LENGTH as PasswordMaxLength,
  checkPasswordComplexity,
} from '../../models/Password';
import {
  DateOfBirth,
  PATTERN as DateOfBirthPattern,
} from '../../models/DateOfBirth';
import {
  changePassword,
  ChangePasswordResponseStatus,
} from '../../api/changePassword';
import PasswordField from '../../components/PasswordField/PasswordField';
import InputFieldAddon from '../../components/InputField/InputFieldAddon';
import PasswordRules from '../../components/PasswordRules/PasswordRules';
import DateField from '../../components/DateField/DateField';
import { ErrorType } from './PasswordRenewError/PasswordRenewError';
import { useToken } from '../../hooks/useToken';
import PasswordRenewFormError from './PasswordRenewFormError/PasswordRenewFormError';
import Button from '../../components/Button/Button';
import { createTrackingObject } from '../../trackingService';
import useSourceContext from '../../hooks/useSourceContext';

interface PasswordResetRequestForm {
  password: Password;
  dateOfBirth: DateOfBirth;
}

const MAXIMUM_ALLOWED_ATTEMPTS = 3;

export interface PasswordRenewProps {
  setTrackingObject: (trackingObject: Record<string, string>) => void;
}

const PasswordRenew: React.FC<PasswordRenewProps> = ({ setTrackingObject }) => {
  const navigate = useNavigate();
  const form = useForm<PasswordResetRequestForm>({ criteriaMode: 'all' });
  const { errors, isSubmitting } = form.formState;
  const password = form.watch('password');
  const [errorMessage, setErrorMessage] = useState<
    ChangePasswordResponseStatus | undefined
  >();
  const [errorCount, setErrorCount] = useState<number>(0);
  const sourceContext = useSourceContext();
  const {
    token,
    isValidating: isTokenValidating,
    isValid: isTokenValid,
  } = useToken(sourceContext);

  const [searchParams] = useSearchParams();
  const customerUid = searchParams.get('uid');

  useEffect(() => {
    if (form.formState.dirtyFields.password) {
      form.trigger('password');
    }
  }, [password, form]);

  useEffect(() => {
    if (!isTokenValidating && (!token || !isTokenValid)) {
      navigateToErrorPage('EXPIRED_TOKEN');
    }
  });

  const openInfoModal = (event: React.SyntheticEvent, modalId: string) => {
    event.preventDefault();
    const modal = document.querySelector(modalId) as CuiModalJSX.CuiModal;
    if (modal) {
      modal.isOpen = !modal.isOpen;
    }
  };

  const navigateToErrorPage = (errorType: ErrorType) => {
    return navigate('error', {
      state: {
        errorType,
      },
    });
  };

  const onSubmit: SubmitHandler<PasswordResetRequestForm> = ({
    password,
    dateOfBirth,
  }) => {
    if (!token) {
      throw new Error('Missing token');
    }

    return changePassword(
      password,
      dateOfBirth,
      token,
      customerUid,
      sourceContext,
    )
      .then(function () {
        setTrackingObject(
          createTrackingObject(
            'login.passwort-vergessen-email.passwort-aendern.erfolgreich',
            undefined,
          ),
        );
        return navigate('success');
      })
      .catch(function (error) {
        setTrackingObject(
          createTrackingObject(
            'login.passwort-vergessen-email.passwort-aendern.fehler',
            undefined,
          ),
        );
        if (error === 'TECHNICAL_ERROR') {
          return navigateToErrorPage('TECHNICAL_ERROR');
        }
        if (errorCount >= MAXIMUM_ALLOWED_ATTEMPTS) {
          return navigateToErrorPage('FAILED_VERIFICATION_ERROR');
        }
        setErrorMessage(error);
        setErrorCount((prevState) => prevState + 1);
      });
  };

  return (
    <FormProvider {...form}>
      <div className="password-renew">
        {(isSubmitting || isTokenValidating) && (
          <div className="loading-overlay loading-overlay--bright">
            <div className="loading-icon loading-icon--centered" />
          </div>
        )}
        <h3
          className="password-renew__title"
          data-testid="password-renew-title"
        >
          Neues Passwort
        </h3>
        <div className="password-renew__content">
          <div className="password-renew__description">
            <p>Gib ein neues Passwort für dein Kundencenter meincongstar an.</p>
            {errorMessage && (
              <p className="password-renew__error-notice notice notice--error">
                <PasswordRenewFormError error={errorMessage} />
              </p>
            )}
          </div>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            autoComplete="off"
            noValidate
            className={'password-renew__form'}
          >
            <PasswordField
              {...form.register('password', {
                required: true,
                minLength: PasswordMinLength,
                maxLength: PasswordMaxLength,
                pattern: PasswordPattern,
                validate: {
                  complexity: checkPasswordComplexity,
                },
              })}
              name="password"
              label="Neues Passwort"
              id="password"
              className={classNames(errors.password && 'input--error')}
              helperText={
                <>
                  {errors.password?.types?.required && (
                    <div
                      className={
                        'notice notice--small notice--error icon--close mt-1'
                      }
                    >
                      Bitte gib ein Passwort ein.
                    </div>
                  )}
                  {form.formState.dirtyFields.password &&
                    errors.password?.type !== 'required' && <PasswordRules />}
                </>
              }
            />

            <DateField
              {...form.register('dateOfBirth', {
                required: 'Bitte gib ein Geburtsdatum ein.',
                pattern: {
                  value: DateOfBirthPattern,
                  message:
                    'Bitte überprüfe das eingegebene Format. (TT.MM.JJJJ)',
                },
              })}
              name="dateOfBirth"
              label="Geburtsdatum (TT.MM.JJJJ)"
              id="dateOfBirth"
              className={classNames(errors.dateOfBirth && 'input--error')}
              addon={
                <InputFieldAddon
                  iconClass="icon--form-help"
                  data-testid="dateOfBirth-help"
                  onClick={(event) =>
                    openInfoModal(event, 'cui-modal#dateOfBirthInfoModal')
                  }
                />
              }
              helperText={
                <>
                  {errors.dateOfBirth && (
                    <div className="notice notice--small notice--error icon--close mt-1">
                      {errors.dateOfBirth.message}
                    </div>
                  )}
                </>
              }
            />
            <cui-modal id="dateOfBirthInfoModal" headline="Geburtsdatum">
              <content-loader-container content-id="enlYSUpyTlVEZkpYMW9FZkN6US9FUT09" />
            </cui-modal>

            <div className="row">
              <div className="password-renew__submit col-12 col-md-5">
                <Button type={'submit'} width={'fullWidth'}>
                  Passwort ändern
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </FormProvider>
  );
};

export default PasswordRenew;
